import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import NewDealDialog from "@/components/deals/NewDealDialog";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { usePostDeal } from "@/hooks/useDeals";
import { useBillingGate } from "@/hooks/useBillingGate";
import { NEW_DEAL_EMPTY_FORM } from "@/constant/deals.constant";
import { DEFAULT_COUNTRY, toE164 } from "@/lib/phone";
import { DealPhase } from "@/enum/common.enum";

import type { ComponentProps, ReactNode } from "react";

interface NewDealLauncherProps {
  className?: string;
  size?: ComponentProps<typeof Button>["size"];
  testId?: string;
  children?: ReactNode;
  ariaLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

export default function NewDealLauncher({ className, size = "lg", testId = "new-deal-btn", children = "New deal", ariaLabel, open: openProp, onOpenChange: onOpenChangeProp, hideTrigger = false }: NewDealLauncherProps) {
  const navigate = useNavigate();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = onOpenChangeProp ?? setOpenState;
  const [form, setForm] = useState(NEW_DEAL_EMPTY_FORM);
  const { mutate: postDeal, isPending: creating } = usePostDeal();
  // The server will refuse this with a 402 while billing has lapsed. Letting
  // someone fill in the whole wizard only to have it rejected on submit is a
  // worse experience than saying so on the button.
  const { blocked, blockedReason } = useBillingGate();

  const createDeal = () => {
    // Two clicks on Create deal used to mean two POSTs and two deals — the
    // duplicate-deal report. The button below is disabled while this is in
    // flight; this guard is the belt to that braces, since the footer button
    // submits a form and Enter can fire it too.
    if (creating) return;
    const address = [
      form.address,
      form.city,
      [form.state, form.zip_code].filter(Boolean).join(" "),
    ].filter(Boolean).join(", ");
    const payload = {
      // Empty is allowed for a buyer with no property yet. The server fills in
      // a readable stand-in rather than storing "" — `address` is the display
      // key for deal cards, email subjects, search and the client's own portal
      // header, and a blank one would surface as a blank row everywhere.
      address,
      city: form.city,
      state: form.state,
      zip_code: form.zip_code,
      client_name: form.client_name.trim(),
      client_email: form.client_email.trim(),
      client_phone: form.client_phone ? toE164(form.client_phone, form.client_phone_country || DEFAULT_COUNTRY) ?? "" : "",
      additional_clients: (form.additional_clients || []).map((c) => ({
        name: c.name.trim(),
        email: c.email.trim(),
        phone: c.phone ? toE164(c.phone, c.phone_country || DEFAULT_COUNTRY) ?? "" : "",
      })),
      co_agent_invite: form.co_agent_email ? {
        name: form.co_agent_name.trim(),
        email: form.co_agent_email.trim(),
        phone: form.co_agent_phone ? toE164(form.co_agent_phone, form.co_agent_phone_country || DEFAULT_COUNTRY) ?? "" : "",
      } : null,
      deal_type: form.deal_type,
      financing: form.financing,
      // Step 1's "already under contract" flag wins over the pathway default,
      // which is also what makes the address mandatory on that path.
      phase: (form.under_contract ? DealPhase.UNDER_CONTRACT : form.phase) as DealPhase,
      list_price: form.list_price ? Number(form.list_price) : null,
      close_date: form.under_contract && form.close_date ? form.close_date : null,
      // Seeds the other side's agent in Contract Details even when no email
      // was given to invite them.
      other_agent_name: form.co_agent_name.trim(),
      hero_image_url: form.hero_image_url || "",
    };
    postDeal(payload, {
      onSuccess: (res) => {
        setOpen(false);
        setForm(NEW_DEAL_EMPTY_FORM);
        if (res.data) navigate(`/deals/${res.data.id}`);
      },
    });
  };

  const trigger = (
    <Button
      type="button"
      variant="dashboardPrimary"
      size={size}
      onClick={() => setOpen(true)}
      disabled={blocked}
      className={className}
      data-testid={testId}
      aria-label={ariaLabel}
    >
      <Plus data-icon="inline-start" /> {children}
    </Button>
  );

  return (
    <>
      {hideTrigger ? null : blocked ? (
        // Radix's tooltip context has no default value, so Root throws without a
        // Provider ancestor and none is mounted at the app root. Scoped here
        // rather than hoisted, to keep the blast radius at this component.
        <TooltipProvider delayDuration={200}>
          <Tooltip>
          {/* A disabled button fires no pointer events, so the tooltip needs a
              wrapper to hang off — otherwise the explanation is unreachable and
              the button just looks broken. */}
            <TooltipTrigger asChild>
              <span className={className} tabIndex={0} data-testid={`${testId}-blocked`}>
                {trigger}
              </span>
            </TooltipTrigger>
            <TooltipContent>{blockedReason}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : trigger}
      <NewDealDialog open={open} onOpenChange={setOpen} form={form} setForm={setForm} onSubmit={createDeal} busy={creating} />
    </>
  );
}
